import React, { useState } from 'react';
import clsx from 'clsx';
import Dialog from '@material-ui/core/Dialog';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { useDispatch, useSelector } from 'react-redux';

import usePricing from 'hooks/usePricing';
import Spinner from 'components/common/Spinner';
import { upgrade as upgradeAction } from 'features/collections';
import { trackCustom } from 'utils/Tracker';
import { useStyles } from './MultiUpgradeModal.styles';

TimeAgo.addLocale(en);
const timeAgo = new TimeAgo('en-US');

const money = (n) => `$${(n || 0).toFixed(2)}`;

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const total = Math.round(seconds);
  const pad = (n) => String(n).padStart(2, '0');
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const languageLabel = (code) => {
  const short = (code || '').split('-')[0];
  if (!short) return '—';
  try {
    return (
      new Intl.DisplayNames(['en'], { type: 'language' }).of(short) ||
      short.toUpperCase()
    );
  } catch (e) {
    return short.toUpperCase();
  }
};

export const MultiUpgradeModal = ({ ids = [], oid, uid, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { priceFor } = usePricing();

  const entities = useSelector((state) => state.collections.entities);

  const [selected, setSelected] = useState(() =>
    ids.reduce((acc, id) => ({ ...acc, [id]: true }), {})
  );
  const [status, setStatus] = useState({});
  const [processing, setProcessing] = useState(false);

  const files = ids
    .map((id) => entities[id])
    .filter(Boolean)
    .map((collection) => {
      const language = collection.languageCode || '';
      const duration = collection?.props?.duration ?? null;
      const minutes = duration ? duration / 60 : 0;
      const type = collection.tTranslation ? 'translation' : 'transcription';
      const pricing = priceFor({ language, minutes, type });

      return {
        id: collection.id,
        oid: collection.orgId || oid,
        name: collection.name || 'Untitled',
        uploaded: collection.started
          ? timeAgo.format(collection.started * 1000)
          : null,
        duration: formatDuration(duration),
        language: languageLabel(language),
        rated: pricing.rated,
        rateLabel: pricing.rated ? money(pricing.pricePerMin) : '—',
        price: pricing.price
      };
    });

  const selectedFiles = files.filter((file) => selected[file.id]);
  const total = selectedFiles.reduce((sum, file) => sum + file.price, 0);
  const count = selectedFiles.length;
  const disabled = count === 0 || processing;

  const toggle = (id) => {
    if (processing) return;
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleConfirm = async () => {
    const targets = files.filter(
      (file) => selected[file.id] && status[file.id] !== 'done'
    );
    if (!targets.length || processing) return;

    setProcessing(true);
    trackCustom({
      category: 'User',
      action: 'Attempt Multi Upgrade to Precision',
      data: { oid, count: targets.length }
    });

    for (const file of targets) {
      setStatus((prev) => ({ ...prev, [file.id]: 'upgrading' }));

      // The precision endpoint is single-file only, so we upgrade sequentially
      // to avoid credit/payment races when charging each file.
      const result = await dispatch(
        upgradeAction({ id: file.id, oid: file.oid, uid })
      );

      if (result.error) {
        setStatus((prev) => ({ ...prev, [file.id]: 'error' }));
        setProcessing(false);
        return;
      }

      setStatus((prev) => ({ ...prev, [file.id]: 'done' }));
      setSelected((prev) => ({ ...prev, [file.id]: false }));
    }

    setProcessing(false);
    onClose();
  };

  const selectedLabel =
    count === 0
      ? 'No files selected'
      : `${count} ${count === 1 ? 'file' : 'files'} selected`;

  const ctaLabel = processing
    ? 'Upgrading…'
    : disabled
    ? 'Select files to upgrade'
    : `Upgrade ${count} ${count === 1 ? 'file' : 'files'} for ${money(total)}`;

  return (
    <Dialog
      open
      onClose={processing ? undefined : onClose}
      maxWidth={false}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div>
          <div className={classes.badge}>
            <span className={classes.badgeDot} />
            Precision transcription
          </div>
          <h2 className={classes.title}>Upgrade to Precision</h2>
          <p className={classes.subtitle}>
            Human-reviewed, 99%+ accurate transcripts with verified speaker
            labels and timestamps. Select the files you'd like to upgrade.
          </p>
        </div>
        <button
          type="button"
          className={classes.close}
          onClick={() => !processing && onClose()}
        >
          ✕
        </button>
      </div>

      <div className={classes.tableHead}>
        <div>File</div>
        <div>Duration</div>
        <div>Language</div>
        <div>Price / min</div>
        <div />
      </div>

      <div className={classes.rows}>
        {files.map((file) => {
          const isOn = !!selected[file.id];
          const fileStatus = status[file.id];
          const showCheck = isOn || fileStatus === 'done';
          return (
            <div
              key={file.id}
              role="button"
              tabIndex={0}
              aria-pressed={isOn}
              className={clsx(classes.row, isOn && classes.rowSelected)}
              onClick={() => toggle(file.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggle(file.id);
                }
              }}
            >
              <div className={classes.fileCell}>
                <div className={classes.fileIcon}>♪</div>
                <div style={{ minWidth: 0 }}>
                  <div className={classes.fileName}>{file.name}</div>
                  {file.uploaded && (
                    <div className={classes.fileMeta}>
                      Uploaded {file.uploaded}
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.cell}>{file.duration}</div>
              <div className={classes.cell}>{file.language}</div>
              <div className={classes.rateCell}>{file.rateLabel}</div>
              <div className={classes.checkWrap}>
                {fileStatus === 'upgrading' ? (
                  <Spinner small />
                ) : (
                  <div
                    className={clsx(classes.check, showCheck && classes.checkOn)}
                  >
                    {showCheck ? '✓' : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className={classes.footer}>
        <div>
          <div className={classes.selectedLabel}>{selectedLabel}</div>
          <div className={classes.totalLabel}>{money(total)} total</div>
        </div>
        <div className={classes.actions}>
          <button
            type="button"
            className={classes.cancel}
            onClick={() => !processing && onClose()}
          >
            Cancel
          </button>
          <button
            type="button"
            className={classes.cta}
            disabled={disabled}
            onClick={handleConfirm}
          >
            {processing && <Spinner light small />}
            {ctaLabel}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default MultiUpgradeModal;
